/**
 * 🖼️ AvatarPreview — human-shaped 2D SVG that reflects every builder slider.
 *
 * Feb 2026 upgrade (owner directive: "I was hoping for the avatar to be
 * more IDK human and more like the details in the stats hahaha"):
 *   • Organic silhouette (curved neck, shoulders, waist, hips, thighs)
 *   • Face features: eyebrows, subtle nose, mouth, ears
 *   • Volumized hair with a small highlight
 *   • Muscle striations when muscle_tone >= 4
 *   • Torso width scales with weight_kg (not just build enum)
 *   • Optional facial hair (heritage / style hint)
 *
 * Accessibility overlays (wheelchair, prosthetics, cane, walker,
 * crutches, high-contrast, sensory light-sensitive squint) all still
 * compose. Nothing is a cookie-cutter template.
 */

const SKIN_TONES = {
  light:        "#f4d0b0",
  medium_light: "#e0b48c",
  medium:       "#c08862",
  medium_dark:  "#8a5a3b",
  dark:         "#5c3820",
  deep:         "#3a2213",
};

const HAIR_COLORS = {
  black:    "#1a1a1a",
  brown:    "#5a3721",
  chestnut: "#7a4b2a",
  auburn:   "#8a3f1f",
  blond:    "#d4b062",
  gray:     "#8a8a8a",
  white:    "#e8e8e8",
  red:      "#a83a1c",
  blue:     "#3455a3",
  pink:     "#d97aa7",
  green:    "#3a8a55",
  violet:   "#8a4bb0",
};

// Slightly darken a hex (~15%) for shadow tone
function shade(hex, amt = -0.15) {
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const k = 1 + amt;
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(n * k)));
  const toHex = (n) => clamp(n).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function buildFactor(build) {
  switch (build) {
    case "slim":     return 0.85;
    case "muscular": return 1.15;
    case "powerful": return 1.30;
    case "average":
    default:         return 1.00;
  }
}

// Weight → subtle torso width bump (kg 45-140 range)
function weightFactor(kg) {
  const w = Math.max(40, Math.min(160, kg || 70));
  return 0.9 + ((w - 40) / 120) * 0.35; // 0.9 – 1.25
}

export function AvatarPreview({ params, size = 260 }) {
  const p = params || {};
  const phys = p.physical || {};
  const acc = p.accessibility || {};
  const style = p.style || {};

  const skin = style.skin_hex || SKIN_TONES[style.skin_tone] || SKIN_TONES.medium;
  const skinShade = shade(skin, -0.14);
  const skinDeep = shade(skin, -0.28);
  const hair = style.hair_hex || HAIR_COLORS[style.hair_color] || HAIR_COLORS.brown;
  const hairHi = shade(hair, 0.2);

  const bf = buildFactor(phys.body_build);
  const wf = weightFactor(phys.weight_kg);
  const tone = Math.max(1, Math.min(5, phys.muscle_tone || 3));
  const usesWheelchair = ["manual_wheelchair", "power_wheelchair"].includes(acc.mobility_aid);
  const usesCane = acc.mobility_aid === "cane";
  const usesWalker = acc.mobility_aid === "walker";
  const usesCrutches = acc.mobility_aid === "crutches";
  const usesProstheticLeg = acc.mobility_aid === "prosthetic_leg";

  const prosthetics = new Set((acc.prosthetics || []).map((x) => x?.location));

  const height = Math.max(140, Math.min(210, phys.height_cm || 170));
  const heightScale = 0.9 + ((height - 140) / 70) * 0.2; // 0.9 – 1.1

  // Silhouette metrics
  const shoulderW = 42 * bf;
  const waistW = 26 * bf * wf;
  const hipW = 32 * bf * wf;
  const chestW = 34 * bf * (0.92 + tone * 0.03);
  const legW = 14 * bf;
  const armW = 11 * bf;
  const thighTaper = 4; // how much narrower knees are vs hips
  const showMuscleLines = tone >= 4;
  const hasFacialHair = !!style.facial_hair && style.facial_hair !== "none";

  // Torso path — organic curve: shoulders → armpit → waist → hip
  const cx = 100;
  const torsoTopY = 68;   // just below neck
  const chestY = 92;
  const waistY = 118;
  const hipY = 136;

  const torsoPath = `
    M ${cx - shoulderW / 2} ${torsoTopY}
    Q ${cx - shoulderW / 2 - 2} ${chestY - 4}, ${cx - chestW / 2} ${chestY}
    Q ${cx - waistW / 2 - 2} ${waistY - 2}, ${cx - waistW / 2} ${waistY}
    Q ${cx - hipW / 2 - 1} ${hipY - 2}, ${cx - hipW / 2} ${hipY}
    L ${cx + hipW / 2} ${hipY}
    Q ${cx + waistW / 2 + 1} ${hipY - 2}, ${cx + waistW / 2} ${waistY}
    Q ${cx + chestW / 2 + 2} ${waistY - 2}, ${cx + chestW / 2} ${chestY}
    Q ${cx + shoulderW / 2 + 2} ${chestY - 4}, ${cx + shoulderW / 2} ${torsoTopY}
    Q ${cx + 8} ${torsoTopY - 4}, ${cx} ${torsoTopY - 2}
    Q ${cx - 8} ${torsoTopY - 4}, ${cx - shoulderW / 2} ${torsoTopY}
    Z
  `;

  // Leg helper — organic taper knee bulge → ankle
  const legPath = (side) => {
    const sign = side === "left" ? -1 : 1;
    const topX = cx + sign * (hipW / 2 - legW / 2 - 1);
    const kneeX = topX + sign * -1; // slight inward taper
    return `
      M ${topX - legW / 2} ${hipY}
      Q ${kneeX - legW / 2 - 1} ${hipY + 40}, ${kneeX - (legW - thighTaper) / 2} ${hipY + 55}
      L ${kneeX - (legW - thighTaper) / 2} ${hipY + 68}
      Q ${kneeX - (legW - thighTaper) / 2 + 1} 210, ${kneeX} 214
      L ${kneeX + (legW - thighTaper) / 2} 214
      Q ${kneeX + (legW - thighTaper) / 2 - 1} 210, ${kneeX + (legW - thighTaper) / 2} ${hipY + 68}
      L ${kneeX + (legW - thighTaper) / 2} ${hipY + 55}
      Q ${kneeX + legW / 2 + 1} ${hipY + 40}, ${topX + legW / 2} ${hipY}
      Z
    `;
  };

  return (
    <svg
      viewBox="0 0 200 260"
      width={size}
      height={size}
      className="mx-auto"
      role="img"
      aria-label="Avatar preview"
      data-testid="avatar-preview-svg"
    >
      <defs>
        {/* Radial skin shading for a hint of dimensionality */}
        <radialGradient id="skinGrad" cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor={skin} />
          <stop offset="70%" stopColor={skin} />
          <stop offset="100%" stopColor={skinShade} />
        </radialGradient>
        <linearGradient id="hairGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hairHi} />
          <stop offset="100%" stopColor={hair} />
        </linearGradient>
      </defs>

      {/* Ground shadow */}
      <ellipse cx="100" cy="245" rx="48" ry="6" fill="rgba(0,0,0,0.35)" />

      <g transform={`translate(100,${125}) scale(${heightScale}) translate(-100,-125)`}>
        {/* Neck — smooth column connecting head + torso */}
        <path
          d={`M ${cx - 8} 60 Q ${cx - 8} 68, ${cx - 12} ${torsoTopY + 2} L ${cx + 12} ${torsoTopY + 2} Q ${cx + 8} 68, ${cx + 8} 60 Z`}
          fill="url(#skinGrad)"
          stroke={skinDeep}
          strokeWidth="0.6"
        />

        {/* Ears (peek out unless hair covers them) */}
        {style.hair_style !== "long" && (
          <>
            <ellipse cx="80.5" cy="43" rx="3" ry="5" fill={skinShade} stroke={skinDeep} strokeWidth="0.5" />
            <ellipse cx="119.5" cy="43" rx="3" ry="5" fill={skinShade} stroke={skinDeep} strokeWidth="0.5" />
          </>
        )}

        {/* Head — subtly oval, not a perfect circle */}
        <ellipse cx="100" cy="42" rx="17" ry="20" fill="url(#skinGrad)" stroke={skinDeep} strokeWidth="0.8" />

        {/* Hair — layered volume + soft highlight */}
        {style.hair_style !== "bald" && (
          <>
            <path
              d={
                style.hair_style === "long"
                  ? "M80 40 Q78 20 100 18 Q122 20 120 40 L122 72 Q100 78 78 72 Z"
                  : style.hair_style === "medium"
                  ? "M80 40 Q78 22 100 20 Q122 22 120 40 L122 52 Q100 55 78 52 Z"
                  : style.hair_style === "shaved"
                  ? "M82 36 Q100 28 118 36 L118 40 L82 40 Z"
                  : style.hair_style === "curly"
                  ? "M78 40 Q76 20 100 18 Q124 20 122 40 Q125 42 122 46 Q118 42 116 46 Q112 42 108 46 Q104 42 100 46 Q96 42 92 46 Q88 42 84 46 Q80 42 78 46 Z"
                  : /* short (default) */ "M80 40 Q82 22 100 20 Q118 22 120 40 L120 46 L80 46 Z"
              }
              fill="url(#hairGrad)"
            />
            {/* Small highlight streak */}
            <path
              d="M87 26 Q100 22 113 26"
              stroke={hairHi}
              strokeWidth="1"
              fill="none"
              opacity="0.55"
            />
          </>
        )}

        {/* Eyebrows */}
        <path d="M89 38 Q93 36 97 38" stroke={hair} strokeWidth="1.2" fill="none" opacity="0.85" />
        <path d="M103 38 Q107 36 111 38" stroke={hair} strokeWidth="1.2" fill="none" opacity="0.85" />

        {/* Eyes — sensory cues drive small squints if light_sensitive */}
        {acc.sensory_profile?.light_sensitive ? (
          <>
            <line x1="92" y1="43" x2="98" y2="43" stroke="#000" strokeWidth="1.5" />
            <line x1="102" y1="43" x2="108" y2="43" stroke="#000" strokeWidth="1.5" />
          </>
        ) : (
          <>
            {/* whites */}
            <ellipse cx="94" cy="43" rx="2.2" ry="1.4" fill="#fff" />
            <ellipse cx="106" cy="43" rx="2.2" ry="1.4" fill="#fff" />
            {/* iris */}
            <circle cx="94" cy="43" r="1.3" fill={style.eye_hex || "#3b2f22"} />
            <circle cx="106" cy="43" r="1.3" fill={style.eye_hex || "#3b2f22"} />
            {/* pupil */}
            <circle cx="94" cy="43" r="0.7" fill="#000" />
            <circle cx="106" cy="43" r="0.7" fill="#000" />
          </>
        )}

        {/* Nose — light shading only */}
        <path d="M98 45 Q100 51 102 45" stroke={skinDeep} strokeWidth="0.7" fill="none" opacity="0.7" />
        <path d="M97 51 Q100 52.5 103 51" stroke={skinDeep} strokeWidth="0.5" fill="none" opacity="0.6" />

        {/* Mouth — small closed curve */}
        <path d="M94 55 Q100 58 106 55" stroke={shade(skinDeep, -0.1)} strokeWidth="1.1" fill="none" strokeLinecap="round" />

        {/* Optional facial hair */}
        {hasFacialHair && (
          <path
            d={
              style.facial_hair === "beard"
                ? "M85 52 Q100 68 115 52 Q114 62 100 66 Q86 62 85 52 Z"
                : style.facial_hair === "goatee"
                ? "M96 56 Q100 62 104 56 Q104 60 100 62 Q96 60 96 56 Z"
                : /* mustache */ "M93 53 Q100 55 107 53 Q104 56 100 55 Q96 56 93 53 Z"
            }
            fill={hair}
            opacity="0.9"
          />
        )}

        {/* Torso silhouette — organic curves */}
        <path d={torsoPath} fill="url(#skinGrad)" stroke={skinDeep} strokeWidth="0.9" />

        {/* Muscle striations — light shading only when actually muscular */}
        {showMuscleLines && !usesWheelchair && (
          <g opacity={0.35} data-testid="avatar-muscle-lines">
            {/* Pecs divide */}
            <line x1={cx} y1={chestY - 2} x2={cx} y2={waistY - 6} stroke={skinDeep} strokeWidth="1" />
            {/* Chest curve L / R */}
            <path d={`M ${cx - chestW / 2 + 4} ${chestY + 2} Q ${cx - 6} ${chestY + 6} ${cx - 3} ${chestY + 10}`} stroke={skinDeep} strokeWidth="0.8" fill="none" />
            <path d={`M ${cx + chestW / 2 - 4} ${chestY + 2} Q ${cx + 6} ${chestY + 6} ${cx + 3} ${chestY + 10}`} stroke={skinDeep} strokeWidth="0.8" fill="none" />
            {/* Abs — 3 stacked lines if very toned */}
            {tone >= 5 && (
              <>
                <line x1={cx - 5} y1={waistY - 8} x2={cx + 5} y2={waistY - 8} stroke={skinDeep} strokeWidth="0.6" />
                <line x1={cx - 5} y1={waistY - 2} x2={cx + 5} y2={waistY - 2} stroke={skinDeep} strokeWidth="0.6" />
                <line x1={cx - 5} y1={waistY + 4} x2={cx + 5} y2={waistY + 4} stroke={skinDeep} strokeWidth="0.6" />
              </>
            )}
          </g>
        )}

        {/* Arms — tapered, with subtle bicep bulge when toned */}
        {/* Left */}
        <path
          d={`
            M ${cx - shoulderW / 2 + 2} ${torsoTopY + 4}
            Q ${cx - shoulderW / 2 - armW * 0.4} ${torsoTopY + 22 + (showMuscleLines ? 4 : 0)}, ${cx - shoulderW / 2 - 2} ${torsoTopY + 40}
            L ${cx - shoulderW / 2 - 4} ${torsoTopY + 72}
            Q ${cx - shoulderW / 2 - 3} ${torsoTopY + 78}, ${cx - shoulderW / 2 + 2} ${torsoTopY + 76}
            L ${cx - shoulderW / 2 + armW - 2} ${torsoTopY + 44}
            Q ${cx - shoulderW / 2 + armW + 2} ${torsoTopY + 22}, ${cx - shoulderW / 2 + armW} ${torsoTopY + 4}
            Z
          `}
          fill={prosthetics.has("left_arm") ? "#a8b0c0" : "url(#skinGrad)"}
          stroke={prosthetics.has("left_arm") ? "#2a3040" : skinDeep}
          strokeWidth="0.8"
        />
        {prosthetics.has("left_arm") && (
          <line x1={cx - shoulderW / 2 - 2} y1={torsoTopY + 32} x2={cx - shoulderW / 2 + armW + 2} y2={torsoTopY + 32} stroke="#2a3040" strokeWidth="1" />
        )}
        {/* Right */}
        <path
          d={`
            M ${cx + shoulderW / 2 - 2} ${torsoTopY + 4}
            Q ${cx + shoulderW / 2 + armW * 0.4} ${torsoTopY + 22 + (showMuscleLines ? 4 : 0)}, ${cx + shoulderW / 2 + 2} ${torsoTopY + 40}
            L ${cx + shoulderW / 2 + 4} ${torsoTopY + 72}
            Q ${cx + shoulderW / 2 + 3} ${torsoTopY + 78}, ${cx + shoulderW / 2 - 2} ${torsoTopY + 76}
            L ${cx + shoulderW / 2 - armW + 2} ${torsoTopY + 44}
            Q ${cx + shoulderW / 2 - armW - 2} ${torsoTopY + 22}, ${cx + shoulderW / 2 - armW} ${torsoTopY + 4}
            Z
          `}
          fill={prosthetics.has("right_arm") ? "#a8b0c0" : "url(#skinGrad)"}
          stroke={prosthetics.has("right_arm") ? "#2a3040" : skinDeep}
          strokeWidth="0.8"
        />
        {prosthetics.has("right_arm") && (
          <line x1={cx + shoulderW / 2 - armW - 2} y1={torsoTopY + 32} x2={cx + shoulderW / 2 + 2} y2={torsoTopY + 32} stroke="#2a3040" strokeWidth="1" />
        )}

        {/* Legs / wheelchair */}
        {usesWheelchair ? (
          <g data-testid="avatar-wheelchair">
            <rect x="68" y="132" width="64" height="14" rx="3" fill="#1a2740" stroke="#3a4a70" strokeWidth="1.5" />
            <rect x="122" y="96" width="8" height="42" rx="2" fill="#1a2740" stroke="#3a4a70" strokeWidth="1.5" />
            <circle cx="78" cy="158" r="12" fill="none" stroke="#3a4a70" strokeWidth="2.5" />
            <circle cx="78" cy="158" r="4" fill="#3a4a70" />
            <circle cx="130" cy="162" r="20" fill="none" stroke="#3a4a70" strokeWidth="2.5" />
            <circle cx="130" cy="162" r="6" fill="#3a4a70" />
            {acc.mobility_aid === "manual_wheelchair" && (
              <circle cx="130" cy="162" r="24" fill="none" stroke="#5a6a90" strokeWidth="1.5" />
            )}
            {/* Thighs on the seat */}
            <path d={`M 80 132 Q 82 148, 88 148 L 108 148 Q 114 148, 116 132 Z`} fill="url(#skinGrad)" stroke={skinDeep} strokeWidth="0.8" />
            <rect x="72" y="160" width="32" height="3" rx="1.5" fill="#3a4a70" />
          </g>
        ) : (
          <g data-testid="avatar-legs">
            {/* Left leg */}
            {prosthetics.has("left_leg") || usesProstheticLeg ? (
              <g data-testid="avatar-blade-left">
                <rect x={cx - hipW / 2} y={hipY} width={legW} height="30" rx="3" fill="#a8b0c0" stroke="#2a3040" strokeWidth="1" />
                <path d={`M ${cx - hipW / 2 + 2} ${hipY + 30} Q ${cx - hipW / 2 - 10} ${hipY + 55} ${cx - hipW / 2 - 4} 214 L ${cx - hipW / 2 + 8} 214 Z`} fill="#3a4560" stroke="#1a2030" strokeWidth="1" />
              </g>
            ) : (
              <path d={legPath("left")} fill="url(#skinGrad)" stroke={skinDeep} strokeWidth="0.8" />
            )}
            {/* Right leg */}
            {prosthetics.has("right_leg") ? (
              <g data-testid="avatar-blade-right">
                <rect x={cx + hipW / 2 - legW} y={hipY} width={legW} height="30" rx="3" fill="#a8b0c0" stroke="#2a3040" strokeWidth="1" />
                <path d={`M ${cx + hipW / 2 - legW + 2} ${hipY + 30} Q ${cx + hipW / 2 + 10} ${hipY + 55} ${cx + hipW / 2 + 4} 214 L ${cx + hipW / 2 - 8} 214 Z`} fill="#3a4560" stroke="#1a2030" strokeWidth="1" />
              </g>
            ) : (
              <path d={legPath("right")} fill="url(#skinGrad)" stroke={skinDeep} strokeWidth="0.8" />
            )}
            {/* Quads midline crease when muscular */}
            {showMuscleLines && (
              <>
                <line x1={cx - hipW / 4} y1={hipY + 12} x2={cx - hipW / 4} y2={hipY + 50} stroke={skinDeep} strokeWidth="0.5" opacity="0.4" />
                <line x1={cx + hipW / 4} y1={hipY + 12} x2={cx + hipW / 4} y2={hipY + 50} stroke={skinDeep} strokeWidth="0.5" opacity="0.4" />
              </>
            )}
            {/* Cane / crutches / walker overlays */}
            {usesCane && (
              <g data-testid="avatar-cane">
                <line x1="150" y1="80" x2="152" y2="220" stroke="#8a5a2b" strokeWidth="2.5" />
                <path d="M148 78 Q152 74 156 78" fill="none" stroke="#8a5a2b" strokeWidth="2.5" />
              </g>
            )}
            {usesCrutches && (
              <g data-testid="avatar-crutches">
                <line x1="58" y1="70" x2="60" y2="220" stroke="#c0c0c0" strokeWidth="3" />
                <line x1="140" y1="70" x2="142" y2="220" stroke="#c0c0c0" strokeWidth="3" />
                <rect x="53" y="65" width="12" height="4" rx="2" fill="#8a5a2b" />
                <rect x="135" y="65" width="12" height="4" rx="2" fill="#8a5a2b" />
              </g>
            )}
            {usesWalker && (
              <g data-testid="avatar-walker">
                <rect x="70" y="150" width="60" height="4" rx="2" fill="#a0a0a0" />
                <line x1="72" y1="150" x2="72" y2="220" stroke="#a0a0a0" strokeWidth="3" />
                <line x1="128" y1="150" x2="128" y2="220" stroke="#a0a0a0" strokeWidth="3" />
                <line x1="100" y1="150" x2="100" y2="200" stroke="#a0a0a0" strokeWidth="2" />
              </g>
            )}
          </g>
        )}

        {/* Dominant-hand marker (subtle cuff highlight) */}
        {acc.dominant_hand === "left" && (
          <circle cx={cx - shoulderW / 2 - 2} cy={torsoTopY + 76} r="3" fill="#f59e0b" stroke="#000" strokeWidth="0.5" />
        )}
        {acc.dominant_hand === "right" && (
          <circle cx={cx + shoulderW / 2 + 2} cy={torsoTopY + 76} r="3" fill="#f59e0b" stroke="#000" strokeWidth="0.5" />
        )}

        {/* High-contrast marker (sensory profile) */}
        {acc.sensory_profile?.high_contrast_preferred && (
          <rect x="0" y="0" width="200" height="260" fill="none" stroke="#facc15" strokeWidth="2" strokeDasharray="4 3" />
        )}
      </g>
    </svg>
  );
}

export default AvatarPreview;
